import { Color } from "@const/color.constants";
import type { ColorScale } from "plotly.js";
import tinycolor from "tinycolor2";
import { ThemeUtil } from "./ThemeUtil";

export class ColorUtil {

  /**
   * Graph traces colors
   */
  static readonly colorPalette: string[] = Color.colors;

  /**
   * Color CSS value for no data (heatmap, deleted items)
   */
  static readonly NO_DATA_COLOR = 'lightgray';

  /**
   * Color CSS value for total sum of area plots
   */
  static readonly TOTAL_COLOR = 'black';

  private static readonly COLOR_SCALE: ColorScale = [[0, 'green'], [0.25, 'lightgreen'], [0.5, 'yellow'], [0.75, 'orange'], [1, 'red']];

  private static readonly COLOR_SCALE_DIM: ColorScale = [[0, 'darkgreen'], [0.25, 'green'], [0.5, 'gold'], [0.75, 'darkorange'], [1, 'darkred']];

  /**
   *  Get scaled color array from COLOR_SCALE for provided value
   */
  static readonly scaleColor: (value: number) => number[] = ColorUtil.makeScaleColor(
    (ColorUtil.getColorScale() as [number, string][]).map(value => value[0] * 100),
    (ColorUtil.getColorScale() as [number, string][]).map(value => value[1])
  );

  /**
   * plotly.js/src/components/colorscale/helpers
   */
  private static makeScaleColor(domain: number[], colors: string[]) {
    const range = colors.map(color => {
      const { r, g, b, a } = tinycolor(color).toRgb();
      return [r, g, b, a];
    });
    return (value: number): number[] => {
      if (value <= domain[0]) return [...range[0]];
      const last = domain.length - 1;
      if (value >= domain[last]) return [...range[last]];
      let i = 1;
      while (value > domain[i]) i++;
      const t = (value - domain[i - 1]) / (domain[i] - domain[i - 1]);
      return range[i - 1].map((channel, c) => channel + t * (range[i][c] - channel));
    };
  }

  /**
   * Array containing arrays mapping a normalized value to an rgb, rgba, hex, hsl, hsv, or named color string.
   * At minimum, a mapping for the lowest (0) and highest (1) values are required.
   */
  static getColorScale() {
    return ThemeUtil.isDarkMode() ? ColorUtil.COLOR_SCALE_DIM : ColorUtil.COLOR_SCALE;
  }

  /**
   * Convert color array to rgba string
   * @param colorArray
   * @returns
   */
  static colorArray2rgba(colorArray: number[]) {
    const colorObj = {
      r: colorArray[0],
      g: colorArray[1],
      b: colorArray[2],
      a: colorArray[3]
    };
    return tinycolor(colorObj).toRgbString();
  }

  /**
   * Get contrast color (black or white) for provided color array
   * @param rgb
   * @returns
   */
  static contrastColor(rgb: number[]) {
    const brightness = Math.round(((rgb[0] * 299) +
      (rgb[1] * 587) +
      (rgb[2] * 114)) / 1000);
    return (brightness > 125) ? 'black' : 'white';
  }

  /**
   * Get readable text color (black or white) for any CSS color string
   * (hex, rgb(a), hsl, named, …). Uses WCAG relative luminance with the
   * 0.179 crossover where contrast against black equals contrast against white.
   * @param color any color string parsable by tinycolor
   * @returns '#000000' or '#ffffff'
   */
  static contrastText(color: string) {
    return tinycolor(color).getLuminance() > 0.179 ? '#000000' : '#ffffff';
  }
}